import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Device, LiveGpsUpdate, StreamInfo } from "../api";
import { applyPlaybackMute, VideoPlayer, type PlayerStatus } from "../components/VideoPlayer";
import { channelCameraLabel } from "../components/CameraGrid";
import { useDevices } from "../hooks/useDevices";
import { useLiveGps } from "../hooks/useLiveGps";
import { gpsPoint } from "../lib/presence";
import { useInViewSet } from "../lib/in-view";
import { channelRunState } from "../lib/live-start";
import { clearChannelStartCooldown, requestChannelStart, viewerIdFor } from "../lib/stream-start";
import { resetLiveAttempts, useLiveChannelWatch } from "../lib/live-watch";
import { WALL_PAGE_SIZE, wallPage } from "../lib/wall-page";

const PAGE_SIZE = WALL_PAGE_SIZE;
const SELECT_KEY = "dashcam:videoWall:selected";
const ROTATE_KEY = "dashcam:videoWall:rotateSec";

export interface WallCamera {
  key: string;
  deviceId: string;
  channel: number;
  name: string;
  serial: string;
  side: string;
  online: boolean;
}

export function wallCameras(devices: Device[]): WallCamera[] {
  const out: WallCamera[] = [];
  for (const device of devices) {
    const name = device.label || device.serial_no || device.phone;
    for (const channel of [1, 2] as const) {
      const side = channel === 1 ? "Front" : channel === 2 ? "Rear" : channelCameraLabel(channel);
      out.push({
        key: `${device.id}:${channel}`,
        deviceId: device.id,
        channel,
        name,
        serial: device.serial_no,
        side,
        online: device.online,
      });
    }
  }
  return out;
}

export function wallGridShape(count: number): { cols: number; rows: number } {
  const n = Math.max(1, Math.min(PAGE_SIZE, count));
  if (n <= 1) return { cols: 1, rows: 1 };
  if (n <= 2) return { cols: 2, rows: 1 };
  if (n <= 4) return { cols: 2, rows: 2 };
  if (n <= 6) return { cols: 3, rows: 2 };
  if (n <= 9) return { cols: 3, rows: 3 };
  if (n <= 12) return { cols: 4, rows: 3 };
  return { cols: 4, rows: 4 };
}

function loadSelected(): string[] | null {
  try {
    const raw = sessionStorage.getItem(SELECT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((item): item is string => typeof item === "string");
  } catch {
    return null;
  }
}

function WallAudioIcon({ muted }: { muted: boolean }) {
  return muted ? (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
      <path fill="currentColor" d="M5 10v4h3l4 4V6L8 10H5z" />
      <path fill="currentColor" d="M15.2 9.1 16.6 7.7 20.3 11.4 22 9.7 23.4 11.1 21.7 12.8 23.4 14.5 22 15.9 20.3 14.2 16.6 17.9 15.2 16.5 18.9 12.8z" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
      <path fill="currentColor" d="M5 10v4h3l4 4V6L8 10H5zm11.5 2a3.5 3.5 0 0 0-1.8-3.1v6.2A3.5 3.5 0 0 0 16.5 12zm0-7.3v2.1A6.5 6.5 0 0 1 20 12a6.5 6.5 0 0 1-3.5 5.2v2.1A8.5 8.5 0 0 0 22 12a8.5 8.5 0 0 0-5.5-7.3z" />
    </svg>
  );
}

function tileStatus(
  online: boolean,
  live: boolean,
  player: PlayerStatus | undefined,
  failed: boolean,
  runState?: string,
): "LIVE" | "STARTING" | "OFFLINE" | "FAILED" | "RECOVERING" | "QUEUED" {
  if (player === "live" || live) return "LIVE";
  if (!online) return "OFFLINE";
  if (failed) return "FAILED";
  if (runState === "recovering") return "RECOVERING";
  if (runState === "queued") return "QUEUED";
  return "STARTING";
}

export function VideoWallPage({ go }: { go: (to: string) => void }) {
  const viewerId = useMemo(() => viewerIdFor("wall"), []);
  const { devices, loading, error } = useDevices();
  const { positions } = useLiveGps();
  const cameras = useMemo(() => wallCameras(devices), [devices]);
  const [selected, setSelected] = useState<string[]>(() => loadSelected() ?? []);
  const userTouched = useRef((loadSelected()?.length ?? 0) > 0);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rotateSec, setRotateSec] = useState(() => {
    const n = Number(sessionStorage.getItem(ROTATE_KEY) ?? "0");
    return Number.isFinite(n) && n >= 10 ? n : 0;
  });
  const [fs, setFs] = useState(false);
  const [picker, setPicker] = useState(false);
  const [muted, setMuted] = useState(true);
  const mutedRef = useRef(true);
  mutedRef.current = muted;
  const [players, setPlayers] = useState<Record<string, PlayerStatus>>({});
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (userTouched.current || selected.length > 0) return;
    const initial = cameras.filter((c) => c.online).slice(0, PAGE_SIZE).map((c) => c.key);
    if (initial.length === 0) return;
    setSelected(initial);
  }, [cameras, selected.length]);

  useEffect(() => {
    if (selected.length === 0 && !userTouched.current) return;
    sessionStorage.setItem(SELECT_KEY, JSON.stringify(selected));
  }, [selected]);

  const chosen = useMemo(() => {
    const want = new Set(selected);
    return cameras.filter((c) => want.has(c.key));
  }, [cameras, selected]);

  const pageCount = Math.max(1, Math.ceil(chosen.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = wallPage(chosen, safePage);
  const shape = wallGridShape(visible.length || 1);
  const { observe } = useInViewSet();
  const [promoted, setPromoted] = useState<ReadonlySet<string>>(() => new Set());
  const { streams, startAtRef, queue } = useLiveChannelWatch({
    viewerId,
    targets: visible.map((cam) => ({
      deviceId: cam.deviceId,
      channel: cam.channel,
      online: cam.online,
      // The page slice is the visible set. Intersection must not leave a tile
      // on this page without a start while another tile is still opening.
      startable: true,
      promoted: promoted.has(cam.deviceId),
    })),
    releaseOnHide: false,
  });

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  useEffect(() => {
    if (rotateSec < 10 || pageCount < 2) return;
    const timer = window.setInterval(() => {
      setPage((current) => (current + 1) % pageCount);
    }, rotateSec * 1000);
    return () => window.clearInterval(timer);
  }, [rotateSec, pageCount]);

  useEffect(() => {
    const onChange = () => setFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleWallAudio = useCallback(() => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    const root = rootRef.current;
    if (root) {
      for (const video of root.querySelectorAll("video")) {
        applyPlaybackMute(video, next, null);
        if (!next && video.paused) void video.play().catch(() => undefined);
      }
    }
    setMuted(next);
  }, []);

  const toggleFs = useCallback(async () => {
    const node = rootRef.current;
    if (!node) return;
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await node.requestFullscreen();
    } catch {
      setFs((value) => !value);
    }
  }, []);

  const filtered = cameras.filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${c.name} ${c.serial} ${c.side} ${c.deviceId}`.toLowerCase().includes(q);
  });

  const selectAll = () => {
    userTouched.current = true;
    setSelected(cameras.map((c) => c.key));
  };
  const clearAll = () => {
    userTouched.current = true;
    setSelected([]);
  };
  const toggle = (key: string) => {
    userTouched.current = true;
    setSelected((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
    setPage(0);
  };

  return (
    <div ref={rootRef} className={`video-wall${fs || picker ? "" : ""}${fs ? " is-fullscreen" : ""}`}>
      <header className="vw-bar">
        <div className="vw-title">
          <strong>Video Wall</strong>
          <span>{chosen.length} selected · page {safePage + 1}/{pageCount}</span>
        </div>
        <div className="vw-actions">
          <button type="button" onClick={() => setPicker((open) => !open)}>{picker ? "Hide cameras" : "Select cameras"}</button>
          <label className="vw-rotate">
            Auto rotate
            <select
              value={rotateSec}
              onChange={(e) => {
                const n = Number(e.target.value);
                setRotateSec(n);
                sessionStorage.setItem(ROTATE_KEY, String(n));
              }}
              aria-label="Auto rotate interval"
            >
              <option value={0}>Off</option>
              <option value={10}>10s</option>
              <option value={15}>15s</option>
              <option value={20}>20s</option>
              <option value={30}>30s</option>
            </select>
          </label>
          {pageCount > 1 ? (
            <>
              <button type="button" onClick={() => setPage((safePage - 1 + pageCount) % pageCount)}>Prev</button>
              <button type="button" onClick={() => setPage((safePage + 1) % pageCount)}>Next</button>
            </>
          ) : null}
          <button type="button" className="primary" onClick={() => void toggleFs()}>{fs ? "Exit fullscreen" : "Fullscreen Video Wall"}</button>
          <button type="button" className="ghost" onClick={() => go("/")}>Dashboard</button>
        </div>
      </header>

      {picker ? (
        <div className="vw-picker">
          <div className="vw-picker-tools">
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search camera" aria-label="Search camera" />
            <button type="button" onClick={selectAll}>Select all</button>
            <button type="button" onClick={clearAll}>Clear all</button>
            <span>{selected.length} selected</span>
          </div>
          <div className="vw-picker-list">
            {filtered.map((cam) => (
              <label key={cam.key}>
                <input type="checkbox" checked={selected.includes(cam.key)} onChange={() => toggle(cam.key)} />
                <span>{cam.name}</span>
                <em>{cam.serial}</em>
                <em>{cam.side}</em>
                <em>{cam.online ? "Online" : "Offline"}</em>
              </label>
            ))}
          </div>
        </div>
      ) : null}

      {error ? <div className="banner">{error}</div> : null}
      {loading && cameras.length === 0 ? <div className="banner">Loading cameras…</div> : null}
      {!loading && chosen.length === 0 ? (
        <div className="empty-grid">Select cameras to show on the video wall.</div>
      ) : (
        <div
          className="vw-grid"
          style={{ gridTemplateColumns: `repeat(${shape.cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${shape.rows}, minmax(0, 1fr))` }}
          aria-label="Video wall"
        >
          {visible.map((cam) => {
            const info = streams[cam.deviceId];
            const channel = info?.channels?.find((item) => item.channel_no === cam.channel) ?? null;
            const run = channelRunState({
              deviceOnline: cam.online,
              live: channel?.live,
              publishing: channel?.publishing,
              startedAt: startAtRef.current.get(cam.key) ?? null,
              now: Date.now(),
              phase: channel?.liveStart?.phase,
            });
            const playHeld = queue.hold.includes(cam.key) && !run.failed && run.state !== "recovering";
            const queued = cam.online && run.state === "idle" && queue.position[cam.deviceId] != null && !playHeld;
            return (
            <WallTile
              key={cam.key}
              cam={cam}
              device={devices.find((d) => d.id === cam.deviceId) ?? null}
              info={info}
              failed={run.failed}
              failReason={run.message}
              playHeld={playHeld}
              queuePosition={queue.position[cam.deviceId]}
              runState={queued ? "queued" : run.state}
              onPlay={() => {
                setPromoted((prev) => {
                  if (prev.has(cam.deviceId)) return prev;
                  const next = new Set(prev);
                  next.add(cam.deviceId);
                  return next;
                });
                resetLiveAttempts(viewerId, cam.deviceId, cam.channel);
                clearChannelStartCooldown(cam.deviceId, cam.channel, viewerId);
              }}
              gps={positions[cam.deviceId] ?? null}
              player={players[cam.key]}
              muted={muted}
              viewRef={observe(cam.key)}
              onRetry={() => {
                resetLiveAttempts(viewerId, cam.deviceId, cam.channel);
                startAtRef.current.set(cam.key, Date.now());
                clearChannelStartCooldown(cam.deviceId, cam.channel, viewerId);
                void requestChannelStart(cam.deviceId, cam.channel, viewerId);
              }}
              onStatus={(status) => setPlayers((prev) => (prev[cam.key] === status ? prev : { ...prev, [cam.key]: status }))}
            />
            );
          })}
          <button
            type="button"
            className={`vw-audio ${muted ? "is-muted" : "is-on"}`}
            aria-label={muted ? "Unmute camera audio" : "Mute camera audio"}
            aria-pressed={!muted}
            title={muted ? "Unmute camera audio" : "Mute camera audio"}
            onClick={(e) => {
              e.stopPropagation();
              toggleWallAudio();
            }}
          >
            <WallAudioIcon muted={muted} />
          </button>
        </div>
      )}
    </div>
  );
}

function WallTile({
  cam,
  device,
  info,
  failed,
  failReason,
  runState,
  playHeld = false,
  queuePosition,
  onPlay,
  gps,
  player,
  muted,
  viewRef,
  onRetry,
  onStatus,
}: {
  cam: WallCamera;
  device: Device | null;
  info: StreamInfo | undefined;
  failed: boolean;
  failReason: string;
  runState: "idle" | "starting" | "publishing" | "playing" | "recovering" | "failed" | "offline" | "queued";
  playHeld?: boolean;
  queuePosition?: number;
  onPlay?: () => void;
  gps: LiveGpsUpdate | null;
  player: PlayerStatus | undefined;
  muted: boolean;
  viewRef: (node: HTMLElement | null) => void;
  onRetry: () => void;
  onStatus: (status: PlayerStatus) => void;
}) {
  const channel = info?.channels?.find((item) => item.channel_no === cam.channel) ?? null;
  const stream = channel ? { ...channel, liveFailed: failed, failReason, runState, playHeld, queuePosition } : null;
  const status = tileStatus(cam.online, Boolean(channel?.live) && player === "live", player, failed, runState);
  const point = device ? gpsPoint(device, gps) : null;
  const [menu, setMenu] = useState(false);
  const tileEl = useRef<{ node: HTMLElement | null }>({ node: null });
  const setTile = useCallback(
    (node: HTMLElement | null) => {
      tileEl.current.node = node;
      viewRef(node);
    },
    [viewRef],
  );

  const retry = () => {
    onRetry();
  };

  return (
    <article ref={setTile} className={`vw-tile status-${status.toLowerCase()}`} onClick={() => setMenu((open) => !open)}>
      <div className="vw-stage">
        <VideoPlayer
          deviceLabel={cam.side}
          deviceName={cam.name}
          stream={stream}
          deviceOnline={cam.online}
          traceKey={cam.key}
          selected={false}
          delaySec={info?.targetDelaySec ?? 2}
          embedded
          hideDelay
          muted={muted}
          pageActive
          onRetry={cam.online ? onRetry : undefined}
          onPlay={playHeld ? onPlay : undefined}
          onSelect={() => setMenu((open) => !open)}
          onStatusChange={onStatus}
        />
      </div>
      <div className="vw-label">
        <span className="vw-name">{cam.name}</span>
        <span className="vw-id">{cam.serial}</span>
      </div>
      <footer className="vw-foot">
        <span className={`vw-live ${status.toLowerCase()}`}>● {status}</span>
        <span>{cam.side}</span>
        {!cam.online && point ? <span>Last known</span> : point ? <span>{point.liveFix ? "GPS" : "No GPS fix"}</span> : <span>No GPS fix</span>}
        {status !== "LIVE" ? (
          <button
            type="button"
            className="vw-retry"
            onClick={(e) => {
              e.stopPropagation();
              retry();
            }}
          >
            Retry
          </button>
        ) : null}
      </footer>
      {menu ? (
        <div className="vw-menu" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => {
              const node = tileEl.current.node;
              if (!node) return;
              if (document.fullscreenElement === node) void document.exitFullscreen();
              else void node.requestFullscreen().catch(() => undefined);
            }}
          >
            Fullscreen tile
          </button>
          <button type="button" onClick={retry}>Reconnect</button>
          <p>{cam.name} · {cam.serial} · {cam.side}{point?.liveFix ? "" : " · video does not need a GPS fix"}</p>
        </div>
      ) : null}
    </article>
  );
}
